import React from 'react';
import styled, { css } from 'styled-components';
import { transparentize } from 'polished';
import { motion, AnimateSharedLayout } from 'framer-motion';

import Admin from 'hive-admin';
import FieldReact from 'hive-admin/src/components/FieldReact';

const Choices = styled.div`
  /* border: 1px solid ${({ theme }) => theme.less.borderColor}; */
  border-radius: 8px;
  display: grid;
  overflow-x: scroll;
  gap: ${({ gap }) => gap || 0};
  /* padding: ${({ padding }) => padding || '4px'}; */
  grid-template-columns: repeat(${({ maxColumns, count }) => (
    maxColumns ? maxColumns : count
  )}, 1fr);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(${({ maxColumns, minColumns, count }) => (
      maxColumns
        ? minColumns
          ? Math.floor((maxColumns + minColumns) / 2)
          : Math.max(1, maxColumns - 1)
        : count
    )}, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(${({ maxColumns, minColumns, count }) => (
      maxColumns
        ? minColumns
          ? minColumns
          : Math.max(1, maxColumns - 2)
        : count
    )}, 1fr);
  }
`;

const Choice = styled.div`
  position: relative;
  display: flex;
  height: auto;
  flex-shrink: 0;
  flex: 1;
  text-align: center;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 1 1 auto;
  /* padding: 2px 20px; */
  padding: 20px;
  ${({ aspectRatio }) => aspectRatio && css`
    aspect-ratio: ${aspectRatio};
  `};
  ${({ hasIcon }) => hasIcon && css`
    padding: 8px 20px;
  `};
`;

const Selector = styled(motion.div)`
  position: absolute;
  inset: 0px;
  border: 1px solid ${({ theme }) => theme.less.primaryColor};
  background-color: ${({ theme }) => (
    transparentize(0.9, theme.less.primaryColor)
  )};
  border-radius: 6px;
`;

const Value = styled.div`
  display: flex;
  flex-direction: column;
  align-items: space-between;
  justify-content: center;
  flex: 0 0 auto;
  position: relative;
  user-select: none;
  transition: color 200ms;
  width: 100%;
  color: ${({ theme, checked }) => (
    checked ? theme.less.primaryColor : theme.less.textColor
  )};
  *:hover > & {
    color: ${({ theme }) => theme.less.primaryColor};
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  width: 100%;
  margin: auto;
  padding: 20%;
`;

const Icon = styled.div`
  display: flex;
  width: 100%;
  flex: 1 1 auto;
  aspect-ratio: ${({ aspectRatio }) => aspectRatio || 1};
  max-height: ${({ maxHeight }) => maxHeight || '100%'};
  transition: background-color 200ms;
  background-color: ${({ theme, checked }) => (
  checked ? theme.less.primaryColor : theme.less.textColor
  )};
  *:hover > * > & {
    background-color: ${({ theme }) => theme.less.primaryColor};
  }
  mask: ${({ mask }) => `url(${mask}) no-repeat center / contain`};
`;

export default function InputRadioAdvanced(props) {
  const {
    value,
    onChange,
    choices,
    gap,
    padding,
    minColumns,
    maxColumns,
    choiceAspectRatio,
    iconAspectRatio,
    iconMaxHeight,
    renderChoiceCustomAfter,
  } = props;
  return (
    <AnimateSharedLayout>
      <Choices
        gap={gap}
        padding={padding}
        minColumns={minColumns}
        maxColumns={maxColumns}
        count={choices.length}
      >
        {choices.map(choice => (
          <Choice
            key={choice.value}
            value={choice.value}
            checked={value === choice.value}
            aspectRatio={choiceAspectRatio}
            onClick={() => onChange(choice.value)}
            hasIcon={!!choice.icon}
          >
            {value === choice.value ? (
              <Selector
                layoutId="selector"
                key="selector"
                transition={{
                  type: 'spring',
                  stiffness: 350,
                  damping: 45,
                }}
              />
            ) : null}
            <Value checked={value === choice.value}>
              {choice.icon ? (
                <IconWrapper>
                  <Icon
                    mask={choice.icon}
                    checked={value === choice.value}
                    aspectRatio={iconAspectRatio}
                    maxHeight={iconMaxHeight}
                  />
                </IconWrapper>
              ) : null}
              {choice.label || null}
              {
                renderChoiceCustomAfter
                ? renderChoiceCustomAfter(choice, props)
                : null
              }
            </Value>
          </Choice>
        ))}
      </Choices>
    </AnimateSharedLayout>
  );
}

Admin.addToLibrary(
  'FieldRadioAdvanced',
  (config) => FieldReact.create({
    virtual: [() => false],
    ...config,
    renderContent: (props) => {
      const choices = (
          props.getChoices
        ? props.getChoices(props)
        : (props.choices || [])
      );
      return <InputRadioAdvanced {...props} choices={choices} />;
    },
  }),
);
